import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
} from "@mui/material";
import { Spinner } from "react-bootstrap";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import authService from "../../../utils/authService";
import { resolveMediaUrl } from "../../../utils/mediaUrl";
import { isNestedProfileFieldKey, NestedProfileFieldValueCell } from "./StudentProfileNestedFieldDiff";

/** GET `admin/student-profile-request/:requestId/` success body (Admin — Student Profile Requests). */
export type ProfileRequestStatus = "request_approved" | "review_request" | "closed";

export interface StudentProfileRequestDetail {
  request_id: number;
  student_id: string;
  student_firstname: string;
  student_lastname: string;
  description: string | null;
  status: ProfileRequestStatus;
  json_data: Record<string, unknown> | null;
}

function jsonDataForApproveMap(data: StudentProfileRequestDetail["json_data"]): Record<string, unknown> {
  if (data !== null && typeof data === "object" && !Array.isArray(data)) {
    return data as Record<string, unknown>;
  }
  return {};
}

type FieldEntry = {
  old_value?: unknown;
  new_value?: unknown;
  status?: string | null;
};
type DisplayFieldEntry = {
  key: string;
  entry: FieldEntry;
  mergedKeys: string[];
  usnImageEntry?: FieldEntry;
};

const stripHtml = (s: string) => s.replace(/<[^>]+>/g, "").trim();

const previewVal = (v: unknown): string => {
  if (v === null || v === undefined) return "—";
  if (typeof v === "object") return JSON.stringify(v);
  const str = String(v);
  const cleaned = stripHtml(str);
  return cleaned.length > 400 ? `${cleaned.slice(0, 400)}…` : cleaned;
};

const detailCellPad = { px: 1.5, py: 1 } as const;

const headCellSx = {
  backgroundColor: "#f8fafc",
  borderRight: "1px solid #e8eaef",
  borderBottom: "1px solid #e8eaef",
  fontWeight: 600,
  fontSize: 12,
  ...detailCellPad,
  position: "sticky" as const,
  top: 0,
  zIndex: 1,
};

const bodyCellSx = {
  ...detailCellPad,
  fontSize: 12,
  borderRight: "1px solid #e8eaef",
  borderBottom: "1px solid #e8eaef",
  color: "#333",
  verticalAlign: "top" as const,
};

const approveCheckboxSx = {
  p: 0,
  color: "#9e9e9e",
  "&.Mui-checked": { color: "#4168a3" },
  "&.MuiCheckbox-indeterminate": { color: "#4168a3" },
};

function approveMapsEqual(a: Record<string, boolean>, b: Record<string, boolean>): boolean {
  const keys = Array.from(new Set([...Object.keys(a), ...Object.keys(b)]));
  for (let i = 0; i < keys.length; i++) {
    const k = keys[i];
    if (!!a[k] !== !!b[k]) return false;
  }
  return true;
}

function isProfilePicFieldKey(fieldKey: string): boolean {
  return fieldKey === "profile_pic_url";
}

const PROFILE_SOCIAL_LINK_KEYS = new Set(["leetcode", "hackerrank", "github", "linkedin"]);

function isProfileSocialLinkFieldKey(fieldKey: string): boolean {
  return PROFILE_SOCIAL_LINK_KEYS.has(fieldKey.toLowerCase());
}

function isHttpUrl(value: unknown): value is string {
  return typeof value === "string" && /^https?:\/\//i.test(value.trim());
}

function isPdfUrl(url: string): boolean {
  return /\.pdf(\?|$|#)/i.test(url);
}

const UrlFileCell: React.FC<{ value: unknown }> = ({ value }) => {
  if (!isHttpUrl(value)) {
    return <>{previewVal(value)}</>;
  }

  const raw = value.trim();
  if (isPdfUrl(raw)) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
        <Box
          component="iframe"
          src={resolveMediaUrl(raw)}
          title="PDF preview"
          sx={{
            width: "100%",
            minHeight: 220,
            border: "1px solid #e8eaef",
            borderRadius: 1,
            backgroundColor: "#fff",
          }}
        />
        <a href={raw} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12 }}>
          Open PDF
        </a>
      </Box>
    );
  }

  return (
    <a href={raw} target="_blank" rel="noopener noreferrer" style={{ wordBreak: "break-word", fontSize: 12 }}>
      {raw}
    </a>
  );
};

const ProfileSocialLinkCell: React.FC<{ value: unknown }> = ({ value }) => {
  const raw = value == null ? "" : String(value).trim();
  if (!raw) {
    return (
      <Typography component="span" variant="body2">
        —
      </Typography>
    );
  }
  return (
    <a href={raw} target="_blank" rel="noopener noreferrer" style={{ wordBreak: "break-word", fontSize: 12 }}>
      {raw}
    </a>
  );
};

const ProfilePicUrlCell: React.FC<{ value: unknown }> = ({ value }) => {
  const [broken, setBroken] = useState(false);
  const raw = value == null ? "" : String(value).trim();
  if (!raw) {
    return (
      <Typography component="span" variant="body2">
        —
      </Typography>
    );
  }
  if (broken) {
    return (
      <Typography component="span" variant="body2" sx={{ wordBreak: "break-word" }}>
        {previewVal(value)}
      </Typography>
    );
  }
  return (
    <Box component="span" sx={{ display: "inline-block", lineHeight: 0, verticalAlign: "top" }}>
      <Box
        component="img"
        src={resolveMediaUrl(raw)}
        alt=""
        onError={() => setBroken(true)}
        sx={{
          display: "block",
          maxWidth: 140,
          maxHeight: 140,
          width: "auto",
          height: "auto",
          objectFit: "contain",
          borderRadius: 1,
          border: "1px solid #e8eaef",
        }}
      />
    </Box>
  );
};

const UsnWithImageCell: React.FC<{ usnValue: unknown; usnImageValue?: unknown }> = ({ usnValue, usnImageValue }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
    <Typography component="span" variant="body2" sx={{ color: "#333", wordBreak: "break-word" }}>
      {previewVal(usnValue)}
    </Typography>
    {usnImageValue != null && String(usnImageValue).trim() !== "" ? <ProfilePicUrlCell value={usnImageValue} /> : null}
  </Box>
);

const StudentProfileRequestDetailView: React.FC = () => {
  const { requestId } = useParams<{ requestId: string }>();
  const navigate = useNavigate();

  const [item, setItem] = useState<StudentProfileRequestDetail | null>(null);
  const [approveMap, setApproveMap] = useState<Record<string, boolean>>({});
  /** Last saved / loaded approval state; toggles until Save make approveMap differ */
  const [savedApproveSnapshot, setSavedApproveSnapshot] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [closing, setClosing] = useState(false);
  const [closeError, setCloseError] = useState<string | null>(null);
  const [showCloseDialog, setShowCloseDialog] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [saveRequestDescription, setSaveRequestDescription] = useState("");
  /** Full-screen overlay during save (POST) + refetch (GET), same pattern as MapStudents / Student. */
  const [loadingOverlay, setLoadingOverlay] = useState(false);

  const initApproveFromJson = useCallback((json: Record<string, unknown>) => {
    const next: Record<string, boolean> = {};
    for (const key of Object.keys(json)) {
      const raw = json[key];
      if (raw && typeof raw === "object" && !Array.isArray(raw)) {
        const st = (raw as FieldEntry).status;
        next[key] = st === "approved" || st === "request_approved";
      } else {
        next[key] = false;
      }
    }
    setApproveMap(next);
    setSavedApproveSnapshot({ ...next });
  }, []);

  const reloadDetail = useCallback(async (): Promise<StudentProfileRequestDetail> => {
    if (!requestId) {
      throw new Error("Missing request id");
    }
    const response = await authService.get(`admin/student-profile-request/${requestId}`);
    const data = response.data as StudentProfileRequestDetail;
    setItem(data);
    initApproveFromJson(jsonDataForApproveMap(data.json_data));
    return data;
  }, [requestId, initApproveFromJson]);

  useEffect(() => {
    if (!requestId) {
      navigate("/admin/student-profile-approval", { replace: true });
      return;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        await reloadDetail();
      } catch (e) {
        console.error("Error loading profile request:", e);
        if (!cancelled) {
          const msg =
            axios.isAxiosError(e) && e.response?.status === 404
              ? "Request not found, or it was removed."
              : "Could not load this request.";
          setError(msg);
          setItem(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [requestId, navigate, reloadDetail]);

  const entries = useMemo(() => {
    if (!item?.json_data) return [] as [string, FieldEntry][];
    return Object.entries(item.json_data).map(([k, v]) => {
      const entry: FieldEntry =
        v && typeof v === "object" && !Array.isArray(v) ? (v as FieldEntry) : { old_value: v, new_value: v };
      return [k, entry] as [string, FieldEntry];
    });
  }, [item]);

  const displayEntries = useMemo(() => {
    const byKey = new Map(entries);
    const result: DisplayFieldEntry[] = [];
    for (const [key, entry] of entries) {
      if (key === "usn_image" && byKey.has("usn")) continue;
      if (key === "usn" && byKey.has("usn_image")) {
        result.push({ key, entry, mergedKeys: ["usn", "usn_image"], usnImageEntry: byKey.get("usn_image") });
        continue;
      }
      result.push({ key, entry, mergedKeys: [key] });
    }
    return result;
  }, [entries]);

  const toggleApprove = (keys: string[]) => {
    setApproveMap((prev) => {
      const allOn = keys.every((k) => !!prev[k]);
      const next = { ...prev };
      keys.forEach((k) => {
        next[k] = !allOn;
      });
      return next;
    });
  };

  const approveAllState = useMemo(() => {
    if (displayEntries.length === 0) return { checked: false, indeterminate: false };
    const approvedCount = displayEntries.filter((row) => row.mergedKeys.every((k) => !!approveMap[k])).length;
    if (approvedCount === 0) return { checked: false, indeterminate: false };
    if (approvedCount === displayEntries.length) return { checked: true, indeterminate: false };
    return { checked: false, indeterminate: true };
  }, [displayEntries, approveMap]);

  const toggleApproveAll = () => {
    if (displayEntries.length === 0) return;
    const allOn = displayEntries.every((row) => row.mergedKeys.every((k) => !!approveMap[k]));
    setApproveMap((prev) => {
      const next = { ...prev };
      const value = !allOn;
      displayEntries.forEach((row) => {
        row.mergedKeys.forEach((k) => {
          next[k] = value;
        });
      });
      return next;
    });
  };

  const allFieldsApproved = useMemo(() => {
    if (entries.length === 0) return false;
    return entries.every(([k]) => approveMap[k]);
  }, [entries, approveMap]);

  const hasUnsavedApproveChanges = useMemo(
    () => !approveMapsEqual(approveMap, savedApproveSnapshot),
    [approveMap, savedApproveSnapshot]
  );

  const closeRequestDisabled = closing || loadingOverlay || hasUnsavedApproveChanges;
  const closeRequestTooltip = closing
    ? "Closing request…"
    : hasUnsavedApproveChanges
      ? "Save your approval choices (Save button) before closing this request."
      : "";

  const handleSaveClick = () => {
    if (!item || !requestId || entries.length === 0) return;
    setSaveError(null);
    const rawDesc = item.description;
    setSaveRequestDescription(
      rawDesc == null || String(rawDesc).trim() === "" ? "" : stripHtml(String(rawDesc))
    );
    setShowSaveDialog(true);
  };

  const handleSaveConfirm = async () => {
    if (!item || !requestId) return;
    setSaving(true);
    setLoadingOverlay(true);
    setSaveError(null);
    try {
      const json_data: Record<string, { old_value?: unknown; new_value?: unknown; status: string }> = {};
      for (const [key, entry] of entries) {
        json_data[key] = {
          old_value: entry.old_value,
          new_value: entry.new_value,
          status: approveMap[key] ? "approved" : "rejected",
        };
      }
      const description = saveRequestDescription.trim();
      await authService.post(`admin/student-profile-request/${requestId}/field-statuses/`, {
        student_id: item.student_id,
        json_data,
        ...(description ? { description } : {}),
      });
      await reloadDetail();
      setShowSaveDialog(false);
      setSaveRequestDescription("");
    } catch (e) {
      console.error("Error saving profile request:", e);
      setSaveError(
        axios.isAxiosError(e) && e.response?.data && typeof (e.response.data as { detail?: string }).detail === "string"
          ? (e.response.data as { detail: string }).detail
          : "Save or refresh failed. Check API or try again."
      );
    } finally {
      setSaving(false);
      setLoadingOverlay(false);
    }
  };

  const executeCloseRequest = async (description: string) => {
    if (!requestId) return;
    setClosing(true);
    setCloseError(null);
    try {
      const response = await authService.put(`admin/student-profile-request/${requestId}/approve/`, {
        description,
      });
      const data = response.data as { status?: string; message?: string };
      if (data.status === "closed") {
        setShowCloseDialog(false);
        navigate("/admin/student-profile-approval");
      }
    } catch (e) {
      console.error("Error closing profile request:", e);
      setCloseError("Close request failed. Check API or try again.");
    } finally {
      setClosing(false);
    }
  };

  const handleCloseRequestClick = () => {
    setCloseError(null);
    if (allFieldsApproved) {
      void executeCloseRequest("");
      return;
    }
    setShowCloseDialog(true);
  };

  const handleCloseRequestConfirm = () => {
    void executeCloseRequest("");
  };

  if (loading && !item) {
    return (
      <div className="container-fluid p-0 pe-2" style={{ marginTop: "12px", minHeight: "calc(100vh - 90px)" }}>
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
        >
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </div>
    );
  }

  if (error || !item) {
    return (
      <div className="container-fluid p-0 pe-2" style={{ marginTop: "12px" }}>
        <Card>
          <CardContent style={{ padding: "16px" }}>
            <Typography color="error" variant="body1" sx={{ mb: 2 }}>
              {error || "Not found."}
            </Typography>
            <Button variant="outlined" size="small" onClick={() => navigate("/admin/student-profile-approval")} style={{ textTransform: "none" }}>
              Back to list
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container-fluid p-0 pe-2" style={{ marginTop: "12px" }}>
      {loadingOverlay && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
        >
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      )}
      <Card>
        <CardContent
          style={{
            height: "calc(100vh - 90px)",
            overflow: "hidden",
            padding: "16px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1} flexWrap="wrap" gap={1} style={{ flexShrink: 0 }}>
            <Typography variant="subtitle2" color="text.secondary">
              {[item.student_firstname, item.student_lastname].filter(Boolean).join(" ")} ({item.student_id})
            </Typography>
            <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
              <Button
                variant="outlined"
                size="small"
                onClick={() => navigate("/admin/student-profile-approval")}
                style={{ height: "36px", fontSize: "12px", textTransform: "none", border: "1px solid #ccc", color: "#666" }}
              >
                Back
              </Button>
              <Tooltip title={closeRequestTooltip} placement="top" disableHoverListener={!closeRequestTooltip}>
                <span style={{ display: "inline-flex" }}>
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={handleCloseRequestClick}
                    disabled={closeRequestDisabled}
                    style={{ height: "36px", fontSize: "12px", textTransform: "none" }}
                  >
                    Close request
                  </Button>
                </span>
              </Tooltip>
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={handleSaveClick}
                disabled={loadingOverlay || saving || entries.length === 0}
                style={{ height: "36px", fontSize: "13px", textTransform: "none" }}
              >
                Save
              </Button>
            </Box>
          </Box>

          {saveError && !showSaveDialog && (
            <Typography color="error" variant="body2" sx={{ mb: 1 }}>
              {saveError}
            </Typography>
          )}
          {closeError && !showCloseDialog && (
            <Typography color="error" variant="body2" sx={{ mb: 1 }}>
              {closeError}
            </Typography>
          )}

          <Dialog
            open={showSaveDialog}
            onClose={() => {
              if (!saving && !loadingOverlay) {
                setShowSaveDialog(false);
                setSaveError(null);
              }
            }}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Save approval</DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Description is loaded from the request; edit it if you want to update what is stored with this save.
              </Typography>
              <TextField
                autoFocus
                label="Description"
                placeholder="e.g. Approved name and photo; rejected bio."
                value={saveRequestDescription}
                onChange={(e) => setSaveRequestDescription(e.target.value)}
                multiline
                minRows={4}
                fullWidth
                variant="outlined"
                size="small"
                disabled={saving || loadingOverlay}
              />
              {saveError && (
                <Typography color="error" variant="body2" sx={{ mt: 1.5 }}>
                  {saveError}
                </Typography>
              )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button
                onClick={() => {
                  setShowSaveDialog(false);
                  setSaveError(null);
                }}
                disabled={saving || loadingOverlay}
                style={{ textTransform: "none" }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => void handleSaveConfirm()}
                disabled={saving || loadingOverlay}
                style={{ textTransform: "none" }}
              >
                {saving || loadingOverlay ? "Saving…" : "Save"}
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog
            open={showCloseDialog}
            onClose={() => {
              if (!closing) {
                setShowCloseDialog(false);
                setCloseError(null);
              }
            }}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Close request</DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Not all fields are marked approved. Close this request anyway?
              </Typography>
              {closeError && (
                <Typography color="error" variant="body2" sx={{ mt: 1.5 }}>
                  {closeError}
                </Typography>
              )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button
                onClick={() => {
                  setShowCloseDialog(false);
                  setCloseError(null);
                }}
                disabled={closing}
                style={{ textTransform: "none" }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleCloseRequestConfirm}
                disabled={closing}
                style={{ textTransform: "none" }}
              >
                {closing ? (
                  <>
                    <Spinner animation="border" size="sm" style={{ marginRight: 8 }} />
                    Closing…
                  </>
                ) : (
                  "Close request"
                )}
              </Button>
            </DialogActions>
          </Dialog>

          <TableContainer
            component={Paper}
            className="table-scroll-wrapper"
            elevation={0}
            sx={{
              marginTop: "8px",
              flexGrow: 1,
              overflow: "auto",
              overflowX: "auto",
              WebkitOverflowScrolling: "touch",
              border: "1px solid #e4e7ec",
              borderRadius: "8px",
              bgcolor: "#fff",
              boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
            }}
          >
            <Table size="small" stickyHeader sx={{ minWidth: 520, tableLayout: "fixed", width: "100%" }}>
              <TableHead>
                <TableRow>
                  <TableCell
                    title="Sl No"
                    sx={{
                      ...headCellSx,
                      width: 40,
                      minWidth: 40,
                      maxWidth: 44,
                      whiteSpace: "nowrap",
                      px: 0.75,
                      textAlign: "center",
                    }}
                  >
                    <span style={{ color: "#555", fontSize: 11 }}>Sl</span>
                  </TableCell>
                  <TableCell
                    sx={{
                      ...headCellSx,
                      width: 100,
                      minWidth: 100,
                      maxWidth: 200,
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                    }}
                  >
                    <span style={{ color: "#555" }}>Field</span>
                  </TableCell>
                  <TableCell sx={{ ...headCellSx, minWidth: 220 }}>
                    <span style={{ color: "#555" }}>Old value</span>
                  </TableCell>
                  <TableCell sx={{ ...headCellSx, minWidth: 220 }}>
                    <span style={{ color: "#555" }}>New value</span>
                  </TableCell>
                  <TableCell
                    sx={{
                      ...headCellSx,
                      width: 44,
                      minWidth: 44,
                      maxWidth: 44,
                      textAlign: "center",
                      px: 0.5,
                    }}
                  >
                    <Checkbox
                      size="small"
                      checked={approveAllState.checked}
                      indeterminate={approveAllState.indeterminate}
                      onChange={toggleApproveAll}
                      disabled={entries.length === 0}
                      title="Select all"
                      inputProps={{ "aria-label": "Select all approve checkboxes" }}
                      sx={approveCheckboxSx}
                    />
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayEntries.map(({ key, entry, mergedKeys, usnImageEntry }, index) => (
                  <TableRow
                    key={key}
                    sx={{
                      backgroundColor: index % 2 === 0 ? "#fff" : "#fafafa",
                      "&:hover": { backgroundColor: "#f0f4fb" },
                    }}
                  >
                    <TableCell
                      sx={{
                        ...bodyCellSx,
                        width: 40,
                        minWidth: 40,
                        maxWidth: 44,
                        whiteSpace: "nowrap",
                        px: 0.75,
                        textAlign: "center",
                      }}
                    >
                      {index + 1}
                    </TableCell>
                    <TableCell
                      sx={{
                        ...bodyCellSx,
                        width: 100,
                        minWidth: 100,
                        maxWidth: 200,
                        textTransform: "capitalize",
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                      }}
                    >
                      {key.replace(/_/g, " ")}
                    </TableCell>
                    <TableCell
                      sx={{
                        ...bodyCellSx,
                        minWidth: 220,
                        maxWidth: "unset",
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                      }}
                    >
                      {key === "usn" && usnImageEntry ? (
                        <UsnWithImageCell usnValue={entry.old_value} usnImageValue={usnImageEntry.old_value} />
                      ) : isNestedProfileFieldKey(key) ? (
                        <NestedProfileFieldValueCell fieldKey={key} value={entry.old_value} />
                      ) : isProfilePicFieldKey(key) ? (
                        <ProfilePicUrlCell value={entry.old_value} />
                      ) : isHttpUrl(entry.old_value) ? (
                        <UrlFileCell value={entry.old_value} />
                      ) : isProfileSocialLinkFieldKey(key) ? (
                        <ProfileSocialLinkCell value={entry.old_value} />
                      ) : (
                        previewVal(entry.old_value)
                      )}
                    </TableCell>
                    <TableCell
                      sx={{
                        ...bodyCellSx,
                        minWidth: 220,
                        maxWidth: "unset",
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                      }}
                    >
                      {key === "usn" && usnImageEntry ? (
                        <UsnWithImageCell usnValue={entry.new_value} usnImageValue={usnImageEntry.new_value} />
                      ) : isNestedProfileFieldKey(key) ? (
                        <NestedProfileFieldValueCell fieldKey={key} value={entry.new_value} />
                      ) : isProfilePicFieldKey(key) ? (
                        <ProfilePicUrlCell value={entry.new_value} />
                      ) : isHttpUrl(entry.new_value) ? (
                        <UrlFileCell value={entry.new_value} />
                      ) : isProfileSocialLinkFieldKey(key) ? (
                        <ProfileSocialLinkCell value={entry.new_value} />
                      ) : (
                        previewVal(entry.new_value)
                      )}
                    </TableCell>
                    <TableCell
                      sx={{
                        ...bodyCellSx,
                        width: 44,
                        minWidth: 44,
                        maxWidth: 44,
                        textAlign: "center",
                        px: 0.5,
                      }}
                    >
                      <Checkbox
                        size="small"
                        checked={mergedKeys.every((k) => !!approveMap[k])}
                        onChange={() => toggleApprove(mergedKeys)}
                        inputProps={{ "aria-label": `Approve ${key}` }}
                        sx={approveCheckboxSx}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </div>
  );
};

export default StudentProfileRequestDetailView;
